import React, { useState, useEffect } from "react";
import { useAuthContext } from "../contexts/AuthContext";
import { auth } from "@startupp-cloud/startupp-ai-sdk";
import { tokenStorage, userStorage } from "../utils/secureStorage";

const isDevelopment = process.env.NODE_ENV === "development";

// Utility function to securely extract sidebar parameter from URL
const getSidebarFromUrl = () => {
  try {
    const url = window.location.href;
    // Match any page with structure: /:page/:param1?/:sidebar?/:hidediv?
    // This regex captures the sidebar parameter (second optional parameter)
    const sidebarMatch = url.match(/\/[^\/]+\/[^\/]*\/([^\/\?#]*)/);

    if (sidebarMatch && sidebarMatch[1]) {
      // Sanitize the sidebar parameter to prevent XSS
      const sidebar = sidebarMatch[1].replace(/[<>\"'&]/g, "");
      return sidebar || null;
    }

    return null;
  } catch (error) {
    if (!isDevelopment && window.errorReporting) {
      window.errorReporting.captureException(error);
    }
    return null;
  }
};

const IPCheck = () => {
  const [ipChecked, setIpChecked] = useState(false);
  const [isAllowedIP, setIsAllowedIP] = useState(false);
  const [isAutoLoggingIn, setIsAutoLoggingIn] = useState(false);
  const context = useAuthContext();
  const sidebar = getSidebarFromUrl();

  // Check IP address
  useEffect(() => {
    if (ipChecked) {
      return;
    }

    const checkIP = async () => {
      try {
        const response = await fetch("https://api.ipify.org?format=json");
        const data = await response.json();
        const userIP = data.ip;

        // Check if IP is in allowed list
        const allowedIPs = process.env.REACT_APP_ALLOWED_IPS?.split(",") || [
          "204.225.25.114",
        ];
        const isAllowed = allowedIPs.includes(userIP);

        setIsAllowedIP(isAllowed);
        setIpChecked(true);
      } catch (error) {
        if (!isDevelopment && window.errorReporting) {
          window.errorReporting.captureException(error);
        }
        setIpChecked(true);
      }
    };

    checkIP();
  }, [ipChecked]);

  // Handle auto-login for allowed IPs
  useEffect(() => {
    console.log("----> sidebar:", sidebar);

    if (
      !ipChecked ||
      !isAllowedIP ||
      context.isAuthenticated ||
      context.loading ||
      isAutoLoggingIn
    ) {
      return;
    }

    if (!sidebar) return;

    const attemptAutoLogin = async () => {
      try {
        const currentUrl = window.location.pathname + window.location.search;
        localStorage.setItem("tvUrl", currentUrl);

        setIsAutoLoggingIn(true);

        // Use environment variables for auto-login
        const username = process.env.REACT_APP_API_USER;
        const password = process.env.REACT_APP_API_PASSWORD;

        if (!username || !password) {
          if (!isDevelopment && window.errorReporting) {
            window.errorReporting.captureException(
              new Error("Auto-login credentials not configured")
            );
          }
          return;
        }

        // Use SDK's loginWithCredentials method directly
        const result = await auth.loginWithCredentials(username, password);

        // Store tokens in SDK and secure storage
        if (result && (result.accessToken || result.token)) {
          const tokenData = {
            accessToken: result.accessToken || result.token,
            refreshToken: result.refreshToken,
          };

          // Store in SDK
          auth.setTokens(tokenData);

          // Store in our secure storage
          tokenStorage.setTokens(tokenData);
        }

        // Instead of refreshUser which might cause race conditions,
        // do a clean page refresh to ensure consistent state
        console.log("Auto-login successful, refreshing page...");
        window.location.href = currentUrl;
      } catch (error) {
        console.error("Auto-login failed:", error);
        setIsAutoLoggingIn(false);
        if (!isDevelopment && window.errorReporting) {
          window.errorReporting.captureException(error);
        }
      }
    };

    attemptAutoLogin();
  }, [
    ipChecked,
    isAllowedIP,
    context.isAuthenticated,
    context.loading,
    isAutoLoggingIn,
    sidebar,
  ]);

  return null;
};

export default IPCheck;
